/**
 * ReadGrid — read-only label→value grid (port of the design's NBReadGrid).
 * Renders saved venue values as a record, not a form; used by EditSection's
 * view mode. Empty values read as a muted "Not set".
 */
import type { ReactNode } from 'react';

import { FhBadge, TeacherEye } from './detailBadges';
import { ReadGridValue } from './ReadGridValue';

export interface ReadRow {
  label: string;
  value?: string | number | null;
  /** Render the value in the monospace face (IDs, ZIPs, handles). */
  mono?: boolean;
  /** Show the FareHarbor-mirrored badge next to the label. */
  fh?: boolean;
  /** Show the teacher-visibility badge (true/false), or omit for none. */
  teacher?: boolean;
  /** Render the value as muted italic text — a placeholder, not a real value. */
  muted?: boolean;
  /** Span the full grid width. */
  full?: boolean;
  /** Shown instead of "Not set" when the value is empty. */
  emptyContent?: ReactNode;
}

export function ReadGrid({ rows, cols = 2 }: { rows: ReadRow[]; cols?: number }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`,
        gap: '14px 24px',
      }}
    >
      {rows.map((r) => {
        return (
          <div key={r.label} style={{ minWidth: 0, gridColumn: r.full ? '1 / -1' : undefined }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 3 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  color: 'var(--nb-subtle)',
                }}
              >
                {r.label}
              </span>
              {r.fh && <FhBadge />}
              {r.teacher !== undefined && <TeacherEye on={r.teacher} />}
            </div>
            <ReadGridValue row={r} />
          </div>
        );
      })}
    </div>
  );
}
